import React, { useMemo } from 'react';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';

/**
 * Themed rich-text editor wrapping react-quill-new.
 *
 * Toolbar: headings (1-3), bold/italic/underline/strike, blockquote,
 * code block, ordered + bullet list, indent, alignment, link, font color,
 * background highlight, clean. Image upload is INTENTIONALLY DISABLED
 * (per admin policy) — pasted image URLs still render.
 *
 * Stores HTML. Plain-text values from before the migration render correctly
 * because Quill accepts any text as initial content.
 *
 * Props:
 *   value       — string of HTML (or plain text on legacy rows)
 *   onChange    — (htmlString) => void
 *   placeholder — passed to ReactQuill
 *   rows        — approximate visible rows (controls min-height)
 *   label       — optional label rendered above the editor
 *   className   — extra class for the outer wrapper
 */
export default function RichTextEditor({
  value = '',
  onChange,
  placeholder = '',
  rows = 6,
  label,
  className = '',
}) {
  const modules = useMemo(() => ({
    toolbar: [
      [{ header: [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ color: [] }, { background: [] }],
      [{ list: 'ordered' }, { list: 'bullet' }],
      [{ indent: '-1' }, { indent: '+1' }],
      [{ align: [] }],
      ['blockquote', 'code-block'],
      ['link'],
      ['clean'],
    ],
    clipboard: { matchVisual: false },
  }), []);

  // Quill formats whitelist — keep in sync with toolbar.
  const formats = [
    'header',
    'bold', 'italic', 'underline', 'strike',
    'color', 'background',
    'list', 'indent',
    'align',
    'blockquote', 'code-block',
    'link',
  ];

  const minHeight = Math.max(60, rows * 22);

  return (
    <div className={`rte-wrapper ${className}`}>
      {label && (
        <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--text-secondary)' }}>
          {label}
        </label>
      )}
      <div style={{ '--rte-min-height': `${minHeight}px` }}>
        <ReactQuill
          theme="snow"
          value={value || ''}
          onChange={onChange}
          modules={modules}
          formats={formats}
          placeholder={placeholder}
        />
      </div>
      {/* Scoped styling so the editor blends with the rest of the form. */}
      <style>{`
        .rte-wrapper {
          max-width: 100%;
          overflow: hidden;
        }
        .rte-wrapper .ql-container {
          background: var(--input-bg);
          color: var(--text-primary);
          font-size: 13px;
          font-family: inherit;
          border-color: var(--border-subtle) !important;
          border-bottom-left-radius: 8px;
          border-bottom-right-radius: 8px;
          cursor: text;
        }
        .rte-wrapper .ql-toolbar {
          background: var(--surface-2, var(--input-bg));
          border-color: var(--border-subtle) !important;
          border-top-left-radius: 8px;
          border-top-right-radius: 8px;
          flex-wrap: wrap;
          display: flex;
        }
        .rte-wrapper .ql-toolbar .ql-stroke { stroke: var(--text-secondary); }
        .rte-wrapper .ql-toolbar .ql-fill   { fill:   var(--text-secondary); }
        .rte-wrapper .ql-toolbar .ql-picker-label,
        .rte-wrapper .ql-toolbar .ql-picker-item { color: var(--text-secondary); }
        .rte-wrapper .ql-toolbar button:hover .ql-stroke,
        .rte-wrapper .ql-toolbar button.ql-active .ql-stroke { stroke: var(--brand-primary, #6366f1); }
        .rte-wrapper .ql-toolbar button:hover .ql-fill,
        .rte-wrapper .ql-toolbar button.ql-active .ql-fill { fill: var(--brand-primary, #6366f1); }
        /* The min-height must live on the EDITOR (the contenteditable), not the
           container — otherwise only the typed text is clickable and clicks in
           the empty area below it don't focus the editor. With the editable
           element filling the whole box, clicking anywhere inside focuses it. */
        .rte-wrapper .ql-editor {
          padding: 10px 12px;
          min-height: var(--rte-min-height, 132px);
          cursor: text;
        }
        .rte-wrapper .ql-editor.ql-blank::before {
          color: var(--text-muted);
          font-style: normal;
          left: 12px;
        }
      `}</style>
    </div>
  );
}
